/**
 * API service for Admin operations (Super Admin only)
 */

const API_BASE_URL = 'https://universal-ai-agent-backend.diypa571.workers.dev'

export interface Company {
  id: number
  name: string
  domain: string
  industry?: string
  subscription_status: string
  trial_end_date?: string
  max_agents?: number
  current_agent_count?: number
  owner_email?: string
  owner_name?: string
  owner_user_id?: number
  model_id?: number
  model_name?: string
  model_provider?: string
  created_at?: string
}

export interface AIModel {
  id: number
  name: string
  provider: string
  model_identifier: string
  base_url?: string
  is_active: boolean
  description?: string
  created_at: string
  updated_at: string
}

export interface CreateModelRequest {
  name: string
  provider: string
  model_identifier: string
  api_key?: string
  base_url?: string
  description?: string
}

export interface UpdateModelRequest {
  name?: string
  provider?: string
  model_identifier?: string
  api_key?: string
  base_url?: string
  description?: string
  is_active?: boolean
}

class AdminService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = localStorage.getItem('token')
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`
    }
  }

  async getCompanies(): Promise<Company[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies`, {
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to fetch companies')
    }

    const data = await response.json()
    return data.data || []
  }

  async getCompaniesWithModels(): Promise<Company[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies-with-models`, {
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to fetch companies')
    }

    const data = await response.json()
    return data.data || []
  }

  async getCompanyOwner(companyId: number): Promise<{ id: number; email: string }> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/owner`, {
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to get company owner')
    }

    const data = await response.json()
    return data.data
  }

  async impersonateCompany(userId: string): Promise<{ token: string; user: any; company: any }> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/impersonate/${userId}`, {
      method: 'POST',
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to impersonate company')
    }

    const data = await response.json()
    return data.data
  }

  async getModels(): Promise<AIModel[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models`, {
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to fetch models')
    }

    const data = await response.json()
    return data.data || []
  }

  async createModel(request: CreateModelRequest): Promise<AIModel> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to create model')
    }

    const data = await response.json()
    return data.data
  }

  async updateModel(modelId: number, request: UpdateModelRequest): Promise<AIModel> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models/${modelId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to update model')
    }

    const data = await response.json()
    return data.data
  }

  async deleteModel(modelId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models/${modelId}`, {
      method: 'DELETE',
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to delete model')
    }
  }

  async setCompanyModel(companyId: number, modelId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/model`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ model_id: modelId })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to set company model')
    }
  }
}

export const adminService = new AdminService()

